import { ParsedUrlQuery } from 'querystring';

import { GetStaticPaths, GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';

import { Layout } from '@/components/Layout';
import Collapse from '@/components/shared/Collapse';
import { ContentWithSidebar } from '@/components/shared/ContentWithSidebar';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { PageHeading } from '@/components/shared/PageHeading';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import getPageBanner from '@/helpers/getPageBanner';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { COMMON_LOCALE_PART, FAQ_LOCALE_PART } from '@/locale';
import { IPage, IPageDetailsProps, Locale, SiteMenuItem } from '@/types';

type Questions = {
  title: string;
  description: string;
};
export default function FrequentQuestions({ siteMenu, pageDetails }: IPageDetailsProps) {
  const { t } = useTranslation(FAQ_LOCALE_PART);

  if (!pageDetails) {
    return <EmptyContent />;
  }

  const questions: Questions[] = t('questions', { returnObjects: true }) ?? [];

  return (
    <Layout
      siteMenu={siteMenu}
      seoOptions={{
        title: pageDetails?.name,
        description: 'description',
        thumbnail: pageDetails?.banner,
      }}
      banner={getPageBanner(pageDetails)}
    >
      <ContentWithSidebar siteMenu={siteMenu}>
        <PageHeading>{pageDetails.name}</PageHeading>
        {questions?.map(({ title, description }) => (
          /* TOOD: hack to render new line characters */
          <Collapse key={title} title={title} description={JSON.parse(`{"text": "${description}"}`).text} />
        ))}
      </ContentWithSidebar>
    </Layout>
  );
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    fallback: false,
    paths: getI18nPaths(),
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { locale } = params as IParams;

  const request = api({ locale: locale });

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<IPage>('pages/detail/faq'),
  ]);
  // faq
  const values = responses.map((val) => (val.status === 'fulfilled' ? val.value.data : undefined));
  const [siteMenu, pageDetails] = values;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART, FAQ_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      pageDetails,
    },
  };
};
